'use client';

import * as React from 'react';

import { formatMoneyAuto } from '@/lib/formatters/money';
import type { Currency } from '@/lib/api/admin-currencies';
import { useCurrencyCatalog } from '@/lib/currency/catalog';
import type { INR, USD } from '@/types/money';
import { cn } from '@/lib/util/cn';

/**
 * Money — the ONLY sanctioned way to render an amount.
 *
 * There were ~96 hand-formatted money sites and five different field names for
 * "currency" across the API (base_currency / currency / currency_id /
 * base_currency_id / default_base_currency), so components had no single
 * contract to read and each invented its own. That is how an account card came
 * to print a hardcoded '$' — which is merely invisible on a USD-only box and
 * flatly wrong next to the INR Nirmal Bang accounts.
 *
 * Two deliberate design decisions:
 *
 *  1. `currency` IS REQUIRED. No default, ever. A default is precisely what
 *     let the bug compile — with the prop required, a call site that does not
 *     know its currency fails at BUILD time instead of rendering the wrong
 *     symbol in production.
 *
 *  2. It owns the WHOLE contract — symbol, locale grouping (₹1,00,000 vs
 *     $100,000), sign placement, tabular figures, sign colouring and compact
 *     form. A thin wrapper is one people reach around the moment they need
 *     something slightly different, and then the 96 sites grow back.
 *
 * Arithmetic never happens here. Callers pass a plain number in MAJOR units;
 * see lib/math/money.ts for anything that needs to add.
 */

export type MoneyProps = {
  /** Amount in MAJOR units (rupees, dollars). */
  amount: number;
  /** ISO code, e.g. 'INR' | 'USD'. REQUIRED — see note above. */
  currency: string;
  /** Colour by sign using the direction tokens (never status green/red). */
  colored?: boolean;
  /** Always show a leading + on positives. */
  signed?: boolean;
  /** Compact form: ₹1.2L / ₹3.4Cr. INR only; ignored elsewhere. */
  compact?: boolean;
  /** Decimal places. Defaults to the formatter's per-currency convention. */
  digits?: number;
  /** Drop the currency symbol (for columns whose header already states it). */
  withoutSymbol?: boolean;
  className?: string;
};

/**
 * String form of <Money>, for the places that genuinely cannot take a node —
 * timeline `detail` strings, aria-labels, toasts, tooltips. It shares the
 * SAME formatting core, so the two can never drift; without it those callers
 * would hand-roll a symbol again and the whole point would be lost.
 */
export function moneyText(
  amount: number,
  currency: string,
  opts: { compact?: boolean; digits?: number; withoutSymbol?: boolean } = {},
  known?: Currency | null,
): string {
  return renderMoney(amount, currency, opts, known);
}

function renderMoney(
  amount: number,
  currency: string,
  { compact = false, digits, withoutSymbol = false }:
    { compact?: boolean; digits?: number; withoutSymbol?: boolean },
  known?: Currency | null,
): string {
  const code = currency.toUpperCase();
  const withSymbol = !withoutSymbol;
  if (code === 'INR') {
    return formatMoneyAuto(amount as INR, {
      currency: 'INR', withSymbol, compact,
      ...(digits !== undefined ? { digits } : {}),
    }).text;
  }
  if (code === 'USD') {
    return formatMoneyAuto(amount as USD, {
      currency: 'USD', withSymbol,
      ...(digits !== undefined ? { digits } : {}),
    }).text;
  }
  // Any OTHER currency — and there will be others: a deployment is denominated
  // in whatever its account settles in, so this path is the normal case for
  // every venue we have not special-cased, not an error path.
  //
  // Show the ISO CODE rather than guessing a symbol: an unfamiliar code is a
  // question, a wrong symbol is a lie. ICU is asked for the code explicitly
  // (`currencyDisplay: 'code'`) so the label is derived, never invented.
  //
  // The locale is PINNED to en-US. It used to be `undefined`, i.e. whatever the
  // runtime defaults to — and on an operator machine set to en-IN that renders
  // €100000 as "EUR 1,00,000.00", lakh grouping on a European currency. Same
  // bug as the hardcoded '$', just hiding one branch further down.
  //
  // Passing `currency` also gets each currency's OWN decimal convention for
  // free: JPY and KRW have no minor unit, so ¥1,234.50 is not a rounding
  // nicety, it is a quantity that cannot exist.
  //
  // `known` is the row from the `currencies` catalog when it has loaded. It is
  // the authority on this currency's symbol and minor-unit digits, so a currency
  // seeded into the DB renders properly with no code change here. It is only ever
  // an ENRICHMENT — everything below works without it.
  const abs = Math.abs(amount);
  const minus = amount < 0 ? '−' : '';
  const catalogDigits = known?.decimals;
  const fixedDigits = digits !== undefined
    ? { minimumFractionDigits: digits, maximumFractionDigits: digits }
    : catalogDigits !== undefined
      ? { minimumFractionDigits: catalogDigits, maximumFractionDigits: catalogDigits }
      : {};
  let n: string;
  try {
    n = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code, currencyDisplay: 'code',
      ...fixedDigits,
    }).format(abs);
    // The catalog's symbol beats the ISO code when we have one: '€1,234.50' reads
    // better than 'EUR 1,234.50', and it is DATA, not a guess baked into the UI.
    if (withSymbol && known?.symbol) {
      n = n.replace(code, '').trim();
      return `${minus}${known.symbol}${n}`;
    }
    // ICU emits "EUR 1,234.50" (with a non-breaking space). Strip the code back
    // off when the caller asked for bare figures — the header states the unit.
    if (!withSymbol) {n = n.replace(code, '').trim();}
  } catch {
    // Not a well-formed ISO 4217 code (ICU throws RangeError). Fall back to a
    // plain grouped number, still on the pinned locale, still labelled.
    const plain = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digits ?? 2, maximumFractionDigits: digits ?? 2,
    }).format(abs);
    n = withSymbol ? `${code} ${plain}` : plain;
  }
  return `${minus}${n}`;
}

export function Money({
  amount,
  currency,
  colored = false,
  signed = false,
  compact = false,
  digits,
  withoutSymbol = false,
  className,
}: MoneyProps) {
  const code = currency.toUpperCase();
  const sign = amount > 0 ? 'long' : amount < 0 ? 'short' : 'neutral';
  const known = useCurrencyCatalog()?.get(code) ?? null;

  const text = React.useMemo(
    () => renderMoney(amount, code, { compact, withoutSymbol,
      ...(digits !== undefined ? { digits } : {}) }, known),
    [amount, code, compact, digits, withoutSymbol, known],
  );

  const plus = signed && amount > 0 ? '+' : '';

  return (
    <span
      data-sign={sign}
      // Some currency symbols are strong RTL characters (AED د.إ, ILS ₪). Left
      // to itself the bidi algorithm reorders them WITH the surrounding text, so
      // an amount lands on the wrong side of its own symbol and neighbouring
      // cells visually swap. Isolating each amount keeps it self-contained.
      dir="ltr"
      style={{ unicodeBidi: 'isolate' }}
      className={cn(
        'font-tnum tabular-nums',
        colored && (sign === 'long' ? 'text-long' : sign === 'short' ? 'text-short' : 'text-fg-muted'),
        className,
      )}
    >
      {plus}{text}
    </span>
  );
}
